<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{ width: '30vw' }"
    :header="t('sites.singleSite.financialConfig.cashlessPay.deleteConfirmation.header')"
    :draggable="false"
    :closable="false"
  >
    <Grid>
      <Col12>
        <div class="flex content-center">
          <div class="flex mr-4">
            <span class="material-symbols-rounded text-[2.5rem]">warning</span>
          </div>

          <div class="flex">
            <span>{{
              t('sites.singleSite.financialConfig.cashlessPay.deleteConfirmation.message')
            }}</span>
          </div>
        </div>
      </Col12>

      <Col12>
        <span>{{
          t('sites.singleSite.financialConfig.cashlessPay.deleteConfirmation.deleteInstructions')
        }}</span>
      </Col12>

      <Col12>
        <InputText
          :placeholder="
            t('sites.singleSite.financialConfig.cashlessPay.deleteConfirmation.deletePlaceholder')
          "
          v-model="deleteText"
        />
      </Col12>
    </Grid>

    <template #footer>
      <Button
        :label="t('sites.singleSite.financialConfig.cashlessPay.deleteConfirmation.rejectLabel')"
        text
        :disabled="deleting"
        @click="closeDialog"
      />
      <Button
        :label="t('sites.singleSite.financialConfig.cashlessPay.deleteConfirmation.acceptLabel')"
        severity="danger"
        :disabled="disableConfirm"
        :loading="deleting"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, onUnmounted, ref } from 'vue';
import { useCashlessPay } from '../composables';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { storeToRefs } from 'pinia';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';
import type { ApiResponseError } from '@centric-os/types';
import { useCashlessPayStore, useSiteConfigStore } from '../../../../../routes';

// Set localization
const { t } = useI18n();

// Ref for delete text
const deleteText = ref<string>('');

// Define visible ref
const visible = defineModel({
  type: Boolean,
  required: true,
});

// Define props
const props = defineProps({
  isSiteConfig: {
    type: Boolean,
    required: true,
  },
});

// Set global toast
const { showErrorToast, showSuccessToast } = useGlobalToast();

// Set site store
const siteStore = useSiteStore();
const { site } = storeToRefs(siteStore);

// Set cashless pay store
const cashlessPayStore = useCashlessPayStore();
const { loading: deleting } = useStoreAPIAction(cashlessPayStore, 'saveCashlessPay');

// Set cashless pay composable
const { mapDeletePrivateConfigPayload, mapDeletePublicConfigPayload } = useCashlessPay();

// Method to close dialog
const closeDialog = (): void => {
  deleteText.value = null;
  visible.value = false;
};

// Method to confirm delete
const confirm = async (): Promise<void> => {
  const privatePayload = mapDeletePrivateConfigPayload();
  const publicPayload = mapDeletePublicConfigPayload();

  await cashlessPayStore
    .saveCashlessPay(site.value.id as string, props.isSiteConfig, privatePayload, publicPayload)
    .then(async () => {
      if (props.isSiteConfig) {
        const siteConfigStore = useSiteConfigStore();
        await siteConfigStore.fetchSiteConfigurations(site.value.id);
      }
      showSuccessToast(
        t('sites.singleSite.financialConfig.cashlessPay.deleteConfirmation.success'),
      );
      closeDialog();
    })
    .catch((error: ApiResponseError) => {
      if (error.status) {
        if (error.status >= 400 && error.status < 500) {
          showErrorToast(error?.error ? error?.error : (error?.message as string));
        } else {
          showErrorToast(
            t('sites.singleSite.financialConfig.cashlessPay.deleteConfirmation.error'),
          );
        }
      }
    });
};

// Computed property to disable confirm button
const disableConfirm = computed<boolean>(() => {
  return deleteText.value !== 'REMOVE';
});

onUnmounted(() => {
  deleteText.value = null;
});
</script>
